<template>
    <mobile-template>

        <!-- ══ HEADER ════════════════════════════════════════════════════ -->
        <div class="bg-gray-900 px-4 pt-6 pb-6 fixed top-0 left-0 right-0 z-10">
            <!-- Nav -->
            <div class="flex items-center gap-3 mb-5">
                <button
                    class="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 active:bg-white/20"
                    @click="$router.back()"
                >
                    <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <polyline points="15 18 9 12 15 6" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
                <span class="text-xs font-bold text-white/40 tracking-widest uppercase flex-1">Chantier</span>
                <span
                    v-if="chantier"
                    class="px-3 py-1.5 rounded-full text-xs font-bold"
                    :class="chantier.terminer ? 'bg-green-400/20 text-green-300' : 'bg-orange-400/20 text-orange-300'"
                >
                    {{ chantier.terminer ? 'Terminé' : 'En cours' }}
                </span>
            </div>

            <!-- Client + adresse -->
            <div v-if="client">
                <h1 class="text-3xl font-extrabold text-white leading-tight capitalize">
                    {{ client.prenom }} {{ client.nom }}
                </h1>
                <p v-if="adresse" class="text-white/50 text-sm mt-1.5">
                    {{ adresse.adresse1 }}, {{ adresse.code_postal }} {{ adresse.ville }}
                </p>
                <div v-if="adresse?.acces || adresse?.notes" class="flex flex-wrap gap-2 mt-2">
                    <span v-if="adresse.acces" class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10 text-white/60 text-xs font-semibold">
                        🔑 {{ adresse.acces }}
                    </span>
                    <span v-if="adresse.notes" class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10 text-white/60 text-xs font-semibold">
                        {{ adresse.notes }}
                    </span>
                </div>
            </div>

            <!-- Skeleton -->
            <div v-else-if="loading" class="space-y-3">
                <div class="h-8 w-2/3 bg-white/10 rounded-xl animate-pulse"></div>
                <div class="h-4 w-1/2 bg-white/10 rounded-xl animate-pulse"></div>
            </div>
        </div>

        <!-- ══ LOADING ════════════════════════════════════════════════ -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-24 gap-3">
            <div class="w-8 h-8 border-2 border-slate-300 border-t-orange-500 rounded-full animate-spin"></div>
            <p class="text-slate-500 text-sm font-medium">Chargement…</p>
        </div>

        <!-- ══ ERREUR ═════════════════════════════════════════════════ -->
        <div v-else-if="error" class="mx-4 mt-6 bg-red-50 border border-red-200 rounded-2xl p-5 text-center">
            <p class="text-red-700 font-bold text-base">Impossible de charger le chantier</p>
            <p class="text-red-500 text-sm mt-1">{{ error }}</p>
            <button class="mt-4 px-5 py-2.5 bg-red-600 text-white text-sm font-bold rounded-xl" @click="loadData">Réessayer</button>
        </div>

        <!-- ══ CONTENU ════════════════════════════════════════════════ -->
        <div v-else class="px-4 py-4 space-y-3 pt-[190px]">

            <!-- Filtres -->
            <div class="flex gap-2">
                <button
                    v-for="f in filters"
                    :key="f.value"
                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border-2 transition-all"
                    :class="activeFilter === f.value
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-slate-500 border-slate-200 active:bg-slate-50'"
                    @click="activeFilter = f.value"
                >
                    {{ f.label }}
                    <span
                        class="text-xs font-black px-1.5 py-0.5 rounded-full"
                        :class="activeFilter === f.value ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'"
                    >
                        {{ countByStatus(f.value) }}
                    </span>
                </button>
            </div>

            <!-- Liste interventions -->
            <div class="space-y-2 pb-8">

                <!-- Empty state -->
                <div v-if="filteredInterventions.length === 0" class="bg-white rounded-2xl shadow-sm p-10 flex flex-col items-center text-center">
                    <div class="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                        <svg class="w-7 h-7 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke-linecap="round"/>
                        </svg>
                    </div>
                    <p class="text-slate-700 font-bold">Aucune tâche</p>
                    <p class="text-slate-400 text-sm mt-1">Aucune intervention dans cette catégorie</p>
                </div>

                <!-- Carte intervention -->
                <div
                    v-for="intervention in sortedInterventions"
                    :key="intervention.id"
                    class="bg-white rounded-2xl shadow-sm overflow-hidden"
                    :class="isTermine(intervention) ? 'opacity-60' : ''"
                >
                    <!-- Bande colorée gauche -->
                    <div class="flex items-stretch">
                        <div
                            class="w-1 shrink-0"
                            :class="getTypeConfig(intervention).barClass"
                        ></div>

                        <div class="flex-1 p-4">

                            <!-- Ligne 1 : type + statut -->
                            <div class="flex items-center justify-between gap-2 mb-2">
                                <div class="flex items-center gap-2 flex-wrap">
                                    <span
                                        class="text-xs px-2.5 py-1 rounded-full font-bold border"
                                        :class="getTypeConfig(intervention).badgeClass"
                                    >
                                        {{ getTypeConfig(intervention).label }}
                                    </span>
                                    <span class="text-xs font-mono text-slate-400">{{ intervention.reference }}</span>
                                    <span
                                        v-if="intervention.passage > 1"
                                        class="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold"
                                    >
                                        passage {{ intervention.passage }}
                                    </span>
                                </div>
                                <span
                                    class="text-xs font-bold shrink-0"
                                    :class="getStatutConfig(intervention).textClass"
                                >
                                    <span v-if="isTermine(intervention)">✓ </span>
                                    {{ getStatutConfig(intervention).label }}
                                </span>
                            </div>

                            <!-- Nom intervention -->
                            <p class="text-base font-bold text-slate-900 mb-2">
                                {{ getInterventionNom(intervention) }}
                            </p>

                            <!-- Appareil -->
                            <p v-if="getAppareilLabel(intervention)" class="text-sm text-slate-500 mb-2">
                                {{ getAppareilLabel(intervention) }}
                            </p>

                            <!-- Meta date + durée -->
                            <div class="flex items-center gap-3 text-xs text-slate-400 mb-3">
                                <div class="flex items-center gap-1">
                                    <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                                    </svg>
                                    <span class="font-medium">{{ formatDateMoment(intervention) }}</span>
                                </div>
                                <div v-if="formatDuree(intervention.duree_estimee)" class="flex items-center gap-1">
                                    <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                    <span class="font-medium">{{ formatDuree(intervention.duree_estimee) }}</span>
                                </div>
                            </div>

                            <!-- CTA -->
                            <button
                                v-if="!isTermine(intervention)"
                                class="w-full flex items-center justify-between px-4 py-4 bg-orange-500 text-white text-sm font-extrabold rounded-xl active:bg-orange-600"
                                @click="goToIntervention(intervention)"
                            >
                                <span>{{ getCtaLabel(intervention) }}</span>
                                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path d="M9 18l6-6-6-6" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>

                            <p v-else class="text-xs text-slate-400 font-medium">Intervention clôturée</p>

                        </div>
                    </div>
                </div>

            </div>
        </div>

    </mobile-template>
</template>

<script>
export default {
    name: 'ChantierTaskSelector',

    data() {
        return {
            loading: true,
            error: null,
            chantier: null,
            client: null,
            adresse: null,
            interventions: [],
            activeFilter: 'a_planifier',
            filters: [
                { value: 'a_planifier', label: 'À faire' },
                { value: 'termine',     label: 'Terminé' },
                { value: 'all',         label: 'Tout' },
            ],
            typeConfig: {
                depannage: {
                    label:      'Dépannage',
                    barClass:   'bg-red-400',
                    badgeClass: 'bg-red-50 border-red-200 text-red-700',
                },
                entretien: {
                    label:      'Entretien',
                    barClass:   'bg-emerald-400',
                    badgeClass: 'bg-emerald-50 border-emerald-200 text-emerald-700',
                },
                installation: {
                    label:      'Installation',
                    barClass:   'bg-blue-400',
                    badgeClass: 'bg-blue-50 border-blue-200 text-blue-700',
                },
                diagnostic: {
                    label:      'Diagnostic',
                    barClass:   'bg-amber-400',
                    badgeClass: 'bg-amber-50 border-amber-200 text-amber-700',
                },
                mise_en_service: {
                    label:      'Mise en service',
                    barClass:   'bg-purple-400',
                    badgeClass: 'bg-purple-50 border-purple-200 text-purple-700',
                },
            },
            statutConfig: {
                planifie:    { label: 'Planifiée',   textClass: 'text-blue-600' },
                a_planifier: { label: 'À planifier', textClass: 'text-amber-600' },
                en_cours:    { label: 'En cours',    textClass: 'text-orange-600' },
                termine:     { label: 'Terminée',    textClass: 'text-emerald-600' },
                annule:      { label: 'Annulée',     textClass: 'text-slate-400' },
            },
            momentLabels: {
                morning:   'Matin',
                afternoon: 'Après-midi',
                day:       'Journée',
            },
        };
    },

    computed: {
        chantierId() { return this.$route.params.chantierId; },

        filteredInterventions() {
            if (this.activeFilter === 'all') return this.interventions;
            return this.interventions.filter(i => {
                const code = i.statut?.code || i.statut;
                return code === this.activeFilter;
            });
        },

        sortedInterventions() {
            const open   = this.filteredInterventions.filter(i => !this.isTermine(i));
            const closed = this.filteredInterventions.filter(i =>  this.isTermine(i));
            const byDate = (a, b) => {
                if (!a.date_intervention) return 1;
                if (!b.date_intervention) return -1;
                return new Date(a.date_intervention) - new Date(b.date_intervention);
            };
            return [...open.sort(byDate), ...closed.sort(byDate)];
        },
    },

    created() { this.loadData(); },

    methods: {
        async loadData() {
            this.loading = true;
            this.error = null;
            try {
                const res = await this.$request.post(`/chantiers/${this.chantierId}/view`, {});
                this.chantier      = res.chantier ?? res;
                this.client        = res.client;
                this.adresse       = res.adresse;
                this.interventions = res.interventions ?? [];
            } catch (e) {
                this.error = e?.message || 'Erreur réseau';
            } finally {
                this.loading = false;
            }
        },

        goToIntervention(intervention) {
            this.$router.push(`/intervention/${intervention.id}`);
        },

        countByStatus(filterValue) {
            if (filterValue === 'all') return this.interventions.length;
            return this.interventions.filter(i => {
                const code = i.statut?.code || i.statut;
                return code === filterValue;
            }).length;
        },

        isTermine(intervention) {
            const code = intervention.statut?.code || intervention.statut;
            return code === 'termine' || code === 'facture' || code === 'clos';
        },

        getTypeConfig(intervention) {
            const typeLogique = intervention.intervention_type?.type_logique || '';
            return this.typeConfig[typeLogique] || {
                label:      typeLogique || 'Intervention',
                barClass:   'bg-slate-300',
                badgeClass: 'bg-slate-100 border-slate-200 text-slate-600',
            };
        },

        getStatutConfig(intervention) {
            const code = intervention.statut?.code || intervention.statut || '';
            return this.statutConfig[code] || { label: code, textClass: 'text-slate-500' };
        },

        getInterventionNom(intervention) {
            return intervention.intervention_type?.nom || 'Intervention sans type';
        },

        getAppareilLabel(intervention) {
            const app = intervention.appareil;
            if (!app) return null;
            return [app.marque, app?.app_type?.label].filter(Boolean).join(' — ');
        },

        formatDateMoment(intervention) {
            if (!intervention.date_intervention) return 'Non planifiée';
            const date    = new Date(intervention.date_intervention);
            const dateStr = date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
            if (intervention.heure_debut) return `${dateStr} · ${intervention.heure_debut.slice(0, 5)}`;
            const moment  = this.momentLabels[intervention.moment_day];
            return moment ? `${dateStr} · ${moment}` : dateStr;
        },

        formatDuree(dureeMin) {
            const min = parseInt(dureeMin, 10);
            if (!min) return null;
            if (min < 60) return `${min} min`;
            const h = Math.floor(min / 60);
            const m = min % 60;
            return m ? `${h}h${m}` : `${h}h`;
        },

        getCtaLabel(intervention) {
            const code = intervention.statut?.code || intervention.statut;
            return code === 'en_cours' ? 'Reprendre' : 'Consulter l\'intervention';
        },
    },
};
</script>